import { clockWords as uiClockWords, type TimeFormat } from "@transact/ui";

/**
 * B403 wave-2 residual — the storefront-wide TIME REGISTER, one module.
 *
 * The register was born fixing locale drift ("Wed, Aug 26, 12:30 PM"
 * cart lines): one derivation cures the tickets pills, the departure
 * pills, the cart lines and the picks line together; every consumer
 * imports these words rather than minting an Intl.DateTimeFormat of
 * its own.
 *
 * The locale still owns the WORDS (weekday and month names localize);
 * the register owns the SHAPE ("<Day> <n>[ <Mon>] · <clock>"). B621
 * moved the CLOCK itself to @transact/ui: 12-hour AM/PM by default
 * (RULED 2026-10-01), the tenant's stored timeFormat threading through
 * every face — the h23 face is now the 24h tenant's choice, not the wall.
 */

export interface WhenOptions {
  readonly locale?: string;
  readonly timeZone?: string;
  /** B621 — the tenant's clock; absent = 12h AM/PM, the ruled default. */
  readonly timeFormat?: TimeFormat;
}

const tzOf = (opts: WhenOptions): { timeZone?: string } =>
  opts.timeZone !== undefined ? { timeZone: opts.timeZone } : {};

/**
 * "9:45 PM" (12h, the ruled default) or "21:45" (a 24h tenant) — B621:
 * the fleet's ONE clock lives in @transact/ui; this register re-exports
 * it so every storefront face threads the tenant's stored choice.
 */
export function clockWords(ms: number, opts: WhenOptions): string {
  return uiClockWords(ms, opts);
}

/** "Fri 15" — composed, so no locale reorders the frame's shape. */
export function dayWords(ms: number, opts: WhenOptions): string {
  const weekday = new Intl.DateTimeFormat(opts.locale, { ...tzOf(opts), weekday: "short" }).format(ms);
  const day = new Intl.DateTimeFormat(opts.locale, { ...tzOf(opts), day: "numeric" }).format(ms);
  return `${weekday} ${day}`;
}

/** "Fri 15 Aug" — the confirmation's fuller day, month word included. */
export function dayMonthWords(ms: number, opts: WhenOptions): string {
  const month = new Intl.DateTimeFormat(opts.locale, { ...tzOf(opts), month: "short" }).format(ms);
  return `${dayWords(ms, opts)} ${month}`;
}

/** "Fri 15 · 10:30" — the in-flow departure face (cart lines, picks line). */
export function slotWords(ms: number, opts: WhenOptions): string {
  return `${dayWords(ms, opts)} · ${clockWords(ms, opts)}`;
}

/** "Fri 15 Aug · 10:30" — the confirmation summary's departure face (74:2). */
export function slotFullWords(ms: number, opts: WhenOptions): string {
  return `${dayMonthWords(ms, opts)} · ${clockWords(ms, opts)}`;
}
